'use client';

import { KeyIcon, Trash2Icon, XIcon } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ScrollArea } from './ui/scroll-area';
import { Spinner } from './ui/spinner';
import { type ContentRegistryView, sdk } from '@/app/sdk';
interface ManageContentRegistriesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after any mutation completes so the parent can re-fetch its content list. */
  onRegistriesChanged?: () => void;
}
interface NewRegistryDraft {
  label: string;
  owner: string;
  repo: string;
  branch: string;
  indexPath: string;
  authToken: string;
}
const EMPTY_DRAFT: NewRegistryDraft = {
  label: '',
  owner: '',
  repo: '',
  branch: '',
  indexPath: '',
  authToken: ''
};
export const ManageContentRegistriesDialog = ({
  open,
  onOpenChange,
  onRegistriesChanged
}: ManageContentRegistriesDialogProps) => {
  const [registries, setRegistries] = useState<ContentRegistryView[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [draft, setDraft] = useState<NewRegistryDraft>(EMPTY_DRAFT);
  const [pendingRemove, setPendingRemove] = useState<{
    id: string;
    label: string;
  } | null>(null);
  const [tokenEditId, setTokenEditId] = useState<string | null>(null);
  const [tokenEditValue, setTokenEditValue] = useState('');
  const fetchRegistries = useCallback(async () => {
    if (!sdk?.tools.listContentRegistries) return;
    setLoading(true);
    try {
      const list = await sdk.tools.listContentRegistries();
      setRegistries(list);
    } catch (err) {
      toast.error(`Failed to load registries: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    if (open) {
      void fetchRegistries();
    } else {
      setDraft(EMPTY_DRAFT);
      setTokenEditId(null);
      setTokenEditValue('');
    }
  }, [open, fetchRegistries]);
  const handleAdd = useCallback(async () => {
    if (!sdk?.tools.addContentRegistry || !sdk.tools.setContentRegistryToken) return;
    const owner = draft.owner.trim();
    const repo = draft.repo.trim();
    if (!owner || !repo) {
      toast.error('Owner and repo are required.');
      return;
    }
    const label = draft.label.trim() || `${owner}/${repo}`;
    setSubmitting(true);
    try {
      const res = await sdk.tools.addContentRegistry({
        kind: 'github',
        label,
        owner,
        repo,
        branch: draft.branch.trim() || undefined,
        indexPath: draft.indexPath.trim() || undefined
      });
      if (!res.success) {
        toast.error(`Failed to add registry: ${res.message}`);
        return;
      }
      if (draft.authToken.trim()) {
        const tokenRes = await sdk.tools.setContentRegistryToken(res.id, draft.authToken.trim());
        if (!tokenRes.success) {
          toast.error(`Registry saved but token storage failed: ${tokenRes.message}`);
        }
      }
      toast.success(`Added registry ${label}`);
      setDraft(EMPTY_DRAFT);
      await fetchRegistries();
      onRegistriesChanged?.();
    } finally {
      setSubmitting(false);
    }
  }, [draft, fetchRegistries, onRegistriesChanged]);
  const handleConfirmRemove = useCallback(async () => {
    if (!pendingRemove || !sdk?.tools.removeContentRegistry) return;
    setSubmitting(true);
    try {
      const res = await sdk.tools.removeContentRegistry(pendingRemove.id);
      if (res.success) {
        toast.success(`Removed registry ${pendingRemove.label}`);
        await fetchRegistries();
        onRegistriesChanged?.();
      } else {
        toast.error(`Failed to remove registry: ${res.message}`);
      }
    } finally {
      setPendingRemove(null);
      setSubmitting(false);
    }
  }, [pendingRemove, fetchRegistries, onRegistriesChanged]);
  const handleSaveToken = useCallback(async () => {
    if (!tokenEditId || !sdk?.tools.setContentRegistryToken) return;
    setSubmitting(true);
    try {
      const value = tokenEditValue.trim();
      const res = await sdk.tools.setContentRegistryToken(tokenEditId, value || null);
      if (res.success) {
        toast.success(value ? 'Token saved' : 'Token cleared');
        await fetchRegistries();
        onRegistriesChanged?.();
        setTokenEditId(null);
        setTokenEditValue('');
      } else {
        toast.error(`Failed to update token: ${res.message}`);
      }
    } finally {
      setSubmitting(false);
    }
  }, [tokenEditId, tokenEditValue, fetchRegistries, onRegistriesChanged]);
  const canAdd = !submitting && !!draft.owner.trim() && !!draft.repo.trim();
  return <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Manage Content Registries</DialogTitle>
            <DialogDescription>
              Connect GitHub-hosted asset pack registries. Auth tokens are stored encrypted on this machine and never sent to the renderer.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Configured Registries</div>
              <ScrollArea className="h-[200px] rounded-md border">
                {loading ? <div className="flex items-center justify-center p-6"><Spinner size="sm" /></div> : registries.length === 0 ? <div className="p-4 text-sm text-muted-foreground text-center">No registries configured.</div> : <div className="divide-y">
                    {registries.map(r => <div key={r.id} className="flex items-center gap-2 p-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{r.label}</div>
                          <div className="text-xs text-muted-foreground truncate font-mono">
                            {r.owner}/{r.repo}{r.branch ? `@${r.branch}` : ''}{r.indexPath ? `:${r.indexPath}` : ''}
                          </div>
                        </div>
                        {r.hasAuthToken && <Badge variant="secondary" className="text-[10px]">Token</Badge>}
                        <Button type="button" variant="ghost" size="sm" onClick={() => {
                    setTokenEditId(r.id);
                    setTokenEditValue('');
                  }} disabled={submitting} tooltip={r.hasAuthToken ? 'Replace or clear token' : 'Add token'}>
                          <KeyIcon className="h-3.5 w-3.5" />
                        </Button>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setPendingRemove({
                    id: r.id,
                    label: r.label
                  })} disabled={submitting} className="text-muted-foreground hover:text-destructive" tooltip="Remove registry">
                          <Trash2Icon className="h-3.5 w-3.5" />
                        </Button>
                      </div>)}
                  </div>}
              </ScrollArea>
            </div>

            {tokenEditId && <div className="rounded-md border bg-muted/30 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Auth Token (leave empty to clear)</Label>
                  <Button type="button" variant="ghost" size="sm" onClick={() => {
                setTokenEditId(null);
                setTokenEditValue('');
              }}>
                    <XIcon className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <Input type="password" value={tokenEditValue} onChange={e => setTokenEditValue(e.target.value)} placeholder="ghp_..." disabled={submitting} />
                <div className="flex justify-end">
                  <Button type="button" size="sm" onClick={() => {
                void handleSaveToken();
              }} disabled={submitting}>Save Token</Button>
                </div>
              </div>}

            <div className="rounded-md border p-3 space-y-2">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Add Registry</div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Label</Label>
                  <Input value={draft.label} onChange={e_0 => setDraft({
                  ...draft,
                  label: e_0.target.value
                })} placeholder="My Registry" disabled={submitting} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Owner</Label>
                  <Input value={draft.owner} onChange={e_1 => setDraft({
                  ...draft,
                  owner: e_1.target.value
                })} placeholder="org-or-user" disabled={submitting} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Repo</Label>
                  <Input value={draft.repo} onChange={e_2 => setDraft({
                  ...draft,
                  repo: e_2.target.value
                })} placeholder="repo-name" disabled={submitting} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Branch (optional)</Label>
                  <Input value={draft.branch} onChange={e_3 => setDraft({
                  ...draft,
                  branch: e_3.target.value
                })} placeholder="main" disabled={submitting} />
                </div>
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs">Index Path (optional)</Label>
                  <Input value={draft.indexPath} onChange={e_4 => setDraft({
                  ...draft,
                  indexPath: e_4.target.value
                })} placeholder="registry.json" disabled={submitting} />
                </div>
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs">Auth Token (optional, for private repos)</Label>
                  <Input type="password" value={draft.authToken} onChange={e_5 => setDraft({
                  ...draft,
                  authToken: e_5.target.value
                })} placeholder="ghp_..." disabled={submitting} />
                </div>
              </div>
              <div className="flex justify-end">
                <Button type="button" size="sm" onClick={() => {
                void handleAdd();
              }} disabled={!canAdd}>
                  {submitting ? <Spinner size="sm" /> : 'Add Registry'}
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => onOpenChange(false)} disabled={submitting}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={pendingRemove !== null} onOpenChange={(o: boolean) => !o && !submitting && setPendingRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove registry?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes <span className="font-semibold">{pendingRemove?.label}</span> and clears its stored auth token. Cached pack downloads on disk are kept; remove them from the Browse dialog if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" disabled={submitting} onClick={() => {
            void handleConfirmRemove();
          }}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>;
};
ManageContentRegistriesDialog.displayName = 'ManageContentRegistriesDialog';